import { Table, Input, Select, Space, Button, Typography, Tag, Collapse } from 'antd'
import type { InputProps, TableProps } from 'antd'
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useExportTickets, useTicketList, useTicketStats } from '@/modules/ticket/queries'
import { exportTicketStatsCsv } from '@/services/api/tickets'
import StatusTag from '@/components/feedback/StatusTag'
import ErrorResult from '@/components/feedback/ErrorResult'
import type { TicketItem, TicketStatsByOwner } from '@/types/ticket'

const { Text } = Typography
const DEFAULT_SORT = 'created_at.desc'

function positiveInt(value: string | null, fallback: number) {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

type CompositionSafeInputProps = Omit<InputProps, 'value' | 'onChange'> & {
  value: string
  onValueChange: (value: string) => void
}

function CompositionSafeInput({ value, onValueChange, ...props }: CompositionSafeInputProps) {
  const [draftValue, setDraftValue] = useState(value)
  const composingRef = useRef(false)
  const committedValueRef = useRef(value)

  useEffect(() => {
    committedValueRef.current = value
    if (!composingRef.current) setDraftValue(value)
  }, [value])

  function commit(nextValue: string) {
    if (nextValue === committedValueRef.current) return
    committedValueRef.current = nextValue
    onValueChange(nextValue)
  }

  return (
    <Input
      {...props}
      value={draftValue}
      onChange={(event) => {
        const nextValue = event.target.value
        setDraftValue(nextValue)
        if (!composingRef.current) commit(nextValue)
      }}
      onCompositionStart={() => {
        composingRef.current = true
      }}
      onCompositionEnd={(event) => {
        composingRef.current = false
        const nextValue = event.currentTarget.value
        setDraftValue(nextValue)
        commit(nextValue)
      }}
    />
  )
}

export default function TicketListPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const page = positiveInt(searchParams.get('page'), 1)
  const pageSize = positiveInt(searchParams.get('page_size'), 20)
  const keyword = searchParams.get('keyword') || ''
  const person = searchParams.get('person') || ''
  const createdFrom = searchParams.get('created_from') || ''
  const createdTo = searchParams.get('created_to') || ''
  const statusFilter = searchParams.get('platform_status') || undefined
  const statusGroup = searchParams.get('platform_status_group') || undefined
  const assigneeFilter = searchParams.get('assignee') || undefined
  const techOwnerFilter = searchParams.get('tech_owner') || undefined
  const sortValue = searchParams.get('sort') || DEFAULT_SORT

  function updateSearch(updates: Record<string, string | undefined>, resetPage = true) {
    const next = new URLSearchParams(searchParams)
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined || value === '') next.delete(key)
      else next.set(key, value)
    })
    if (resetPage) next.set('page', '1')
    setSearchParams(next, { replace: true })
  }

  const filterParams = {
    keyword: keyword || undefined,
    person: person || undefined,
    created_from: createdFrom || undefined,
    created_to: createdTo || undefined,
    platform_status: statusFilter,
    platform_status_group: statusGroup,
    assignee: assigneeFilter,
    tech_owner: techOwnerFilter,
    sort_by: sortValue.split('.')[0],
    sort_order: sortValue.split('.')[1],
  }
  const { data, isLoading, isError, error, refetch } = useTicketList({
    page,
    page_size: pageSize,
    ...filterParams,
  })
  const { data: stats, isLoading: statsLoading } = useTicketStats(filterParams)
  const exportMutation = useExportTickets()

  function openStatsDrill(scope: 'assignee' | 'tech_owner', owner: string, status?: string, statusGroupValue?: string) {
    const next = new URLSearchParams(searchParams)
    next.set(scope, owner)
    next.delete(scope === 'assignee' ? 'tech_owner' : 'assignee')
    if (status) {
      next.set('platform_status', status)
      next.delete('platform_status_group')
    } else if (statusGroupValue) {
      next.set('platform_status_group', statusGroupValue)
      next.delete('platform_status')
    } else {
      next.delete('platform_status')
      next.delete('platform_status_group')
    }
    next.set('page', '1')
    setSearchParams(next, { replace: true })
  }

  const renderStatsValue = (
    value: number,
    row: TicketStatsByOwner,
    scope: 'assignee' | 'tech_owner',
    status?: string,
    statusGroupValue?: string,
  ) => {
    if (!value) return <Text type="secondary">0</Text>
    return (
      <Button
        type="link"
        size="small"
        onClick={() => openStatsDrill(scope, row.owner, status, statusGroupValue)}
        style={{ padding: 0 }}
      >
        {value}
      </Button>
    )
  }

  function renderStatsOwner(row: TicketStatsByOwner) {
    const owner = row.owner || 'unassigned'
    const name = String(row.owner_name || '').trim()
    return name && name !== owner ? `${name}(${owner})` : owner
  }

  const buildStatsColumns = (scope: 'assignee' | 'tech_owner'): TableProps<TicketStatsByOwner>['columns'] => [
    { title: '人员', dataIndex: 'owner', width: 180, render: (_: string, row) => renderStatsOwner(row) },
    { title: '总工单', dataIndex: 'total', width: 90, render: (v: number, row) => renderStatsValue(v, row, scope) },
    { title: '已关闭', dataIndex: 'closed', width: 90, render: (v: number, row) => renderStatsValue(v, row, scope, 'closed') },
    { title: '未完结', dataIndex: 'unfinished', width: 90, render: (v: number, row) => renderStatsValue(v, row, scope, undefined, 'unfinished') },
    { title: '处理中', dataIndex: 'doing', width: 90, render: (v: number, row) => renderStatsValue(v, row, scope, 'doing') },
    { title: '待客户确认', dataIndex: 'waiting_customer_confirm', width: 110, render: (v: number, row) => renderStatsValue(v, row, scope, 'waiting_customer_confirm') },
    { title: '待接单', dataIndex: 'waiting_start', width: 90, render: (v: number, row) => renderStatsValue(v, row, scope, 'waiting_start') },
    { title: '已退回', dataIndex: 'returned', width: 90, render: (v: number, row) => renderStatsValue(v, row, scope, 'returned') },
  ]
  const renderStatsTable = (rows: TicketStatsByOwner[], scope: 'assignee' | 'tech_owner') => (
    <Table<TicketStatsByOwner>
      size="small"
      rowKey="owner"
      loading={statsLoading}
      dataSource={rows}
      pagination={false}
      columns={buildStatsColumns(scope)}
    />
  )

  if (isError) return <ErrorResult error={error} onRetry={() => refetch()} />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Space wrap>
        <CompositionSafeInput
          placeholder="搜索工单标题/客户"
          prefix={<SearchOutlined />}
          allowClear
          style={{ width: 260 }}
          value={keyword}
          onValueChange={(value) => updateSearch({ keyword: value })}
        />
        <CompositionSafeInput
          placeholder="搜索人员姓名/账号"
          allowClear
          style={{ width: 180 }}
          value={person}
          onValueChange={(value) => updateSearch({ person: value })}
        />
        <Input
          type="date"
          aria-label="开始日期"
          style={{ width: 150 }}
          value={createdFrom}
          onChange={(e) => updateSearch({ created_from: e.target.value })}
        />
        <Input
          type="date"
          aria-label="结束日期"
          style={{ width: 150 }}
          value={createdTo}
          onChange={(e) => updateSearch({ created_to: e.target.value })}
        />
        <Select
          placeholder="平台状态"
          allowClear
          style={{ width: 150 }}
          value={statusFilter}
          onChange={(v) => updateSearch({ platform_status: v, platform_status_group: undefined })}
          options={[
            { label: '待接单', value: 'waiting_start' },
            { label: '处理中', value: 'doing' },
            { label: '待客户确认', value: 'waiting_customer_confirm' },
            { label: '已退回', value: 'returned' },
            { label: '已关闭', value: 'closed' },
          ]}
        />
        <Select
          aria-label="排序"
          style={{ width: 180 }}
          value={sortValue}
          onChange={(v) => updateSearch({ sort: v })}
          options={[
            { label: '最新建单在前', value: 'created_at.desc' },
            { label: '最早建单在前', value: 'created_at.asc' },
            { label: '最近更新在前', value: 'updated_at.desc' },
            { label: '最近同步在前', value: 'last_synced_at.desc' },
            { label: '最近关闭在前', value: 'task_closed_at.desc' },
          ]}
        />
        <Button
          icon={<DownloadOutlined />}
          loading={exportMutation.isPending}
          onClick={() => exportMutation.mutate(filterParams)}
        >
          导出工单
        </Button>
        <Button
          icon={<DownloadOutlined />}
          disabled={!stats}
          loading={statsLoading}
          onClick={() => stats && exportTicketStatsCsv(stats)}
        >
          导出统计报表
        </Button>
      </Space>

      <Collapse
        defaultActiveKey={[]}
        items={[
          {
            key: 'by_assignee',
            label: <Text strong>按当前执行人统计（当前筛选共 {stats?.total || 0} 单）</Text>,
            children: renderStatsTable(stats?.by_assignee || [], 'assignee'),
          },
          {
            key: 'by_owner',
            label: <Text strong>按第一负责人统计（当前筛选共 {stats?.total || 0} 单）</Text>,
            children: renderStatsTable(stats?.by_owner || [], 'tech_owner'),
          },
        ]}
      />

      <Table<TicketItem>
        size="small"
        rowKey="mapping_id"
        loading={isLoading}
        dataSource={data?.items || []}
        pagination={{
          current: page,
          pageSize,
          total: data?.total || 0,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => updateSearch({ page: String(p), page_size: String(ps) }, false),
        }}
        columns={[
          { title: '禅道ID', dataIndex: 'zentao_task_id', width: 80, render: (v: number) => `#${v}` },
          {
            title: '标题',
            dataIndex: 'task_title',
            width: 360,
            render: (v: string, r: TicketItem) => (
              <Button
                type="link"
                title={v}
                onClick={() => navigate(`/tickets/${r.mapping_id}`, { state: { returnTo: `${location.pathname}${location.search}` } })}
                style={{ height: 'auto', padding: 0, textAlign: 'left', whiteSpace: 'normal', wordBreak: 'break-word' }}
              >
                {v || '-'}
              </Button>
            ),
          },
          { title: '客户', dataIndex: 'customer_name', width: 100 },
          {
            title: '来源标签', dataIndex: 'trigger_source_type', width: 110,
            render: (v: string) => {
              if (v === 'wechat_group_trigger') {
                return <Tag color="processing" style={{ fontSize: 11 }}>微信群触发</Tag>
              }
              return <Text type="secondary" style={{ fontSize: 12 }}>-</Text>
            },
          },
          { title: '平台状态', dataIndex: 'platform_status', width: 120, render: (v: string) => <StatusTag type="platform" status={v} /> },
          { title: '禅道状态', dataIndex: 'zentao_status', width: 90, render: (v: string) => <Text>{v}</Text> },
          { title: '当前执行人', key: 'assignee', width: 90, render: (_: unknown, r: TicketItem) => r.assignee_name || r.assignee || r.assigned_to || <Text type="secondary">-</Text> },
          { title: '第一负责人', key: 'tech_owner', width: 100, render: (_: unknown, r: TicketItem) => r.tech_owner_name || r.tech_owner || <Text type="secondary">-</Text> },
          { title: '创建时间', dataIndex: 'task_created_at', width: 120, render: (v: string) => v ? v.slice(0, 10) : '-' },
          {
            title: '超时风险', dataIndex: 'sla_risk_level', width: 80,
            render: (v: string) => {
              if (v === 'danger') return <Text type="danger">高风险</Text>
              if (v === 'warning') return <Text type="warning">预警</Text>
              return <Text type="secondary">正常</Text>
            },
          },
          {
            title: '下一步', dataIndex: 'next_action_hint', width: 150, ellipsis: true,
            render: (v: string) => v ? <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text> : '-',
          },
          {
            title: '操作', width: 80, render: (_: unknown, r: TicketItem) => (
              <Button type="link" size="small" onClick={() => navigate(`/tickets/${r.mapping_id}`, { state: { returnTo: `${location.pathname}${location.search}` } })}>查看</Button>
            ),
          },
        ]}
      />
    </div>
  )
}
